// Centralised Chart.js registration — called once before any chart renders.
// We keep this in its own file so consumers don't all repeat the imports.

import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  RadarController,
  RadialLinearScale,
  TimeScale,
  Tooltip,
  type ScriptableContext,
} from "chart.js";
import "chartjs-adapter-date-fns";

let registered = false;

export function ensureChartsRegistered() {
  if (registered) return;
  ChartJS.register(
    CategoryScale,
    LinearScale,
    LineElement,
    PointElement,
    // Radar (health radar card, ADR-018 / SPEC-015 §3.3)
    RadarController,
    RadialLinearScale,
    TimeScale,
    Tooltip,
    Legend,
    Filler,
  );
  ChartJS.defaults.color = "#7ab3d0"; // admin-text2
  ChartJS.defaults.font.family =
    "'Noto Sans TC', system-ui, -apple-system, sans-serif";
  registered = true;
}

// Tracked metrics get a personal baseline overlay; the absolute axis range
// is the static lookup below.
//
// IMPORTANT — these bounds describe the value AS STORED in InfluxDB (which is
// what this chart plots), NOT the raw FaceHeart SDK output. The mirror app
// (ResultPage.tsx#toUploadRequest) transforms some fields before upload, so the
// stored range differs from the SDK JSDoc range. Verified against the vendored
// SDK 5.9.3 JSDoc + the mirror upload path (.asp-fact-check.md 2026-06-23):
//   - health_v2_* : SDK 0–5 float, sent raw → stored 0–5 (ADR-030; radar card,
//     not in this line-metric table — the legacy ×20 → health_* 0–100 is removed)
//   - hrv_mean_rr : SDK MEAN_RR is seconds (0–2), sent raw → stored in SECONDS
//   - hrv_hf / hrv_lf : SDK sends ln(HF)/ln(LF) (range −4–8), sent raw →
//     stored values are LOG-SCALE and CAN BE NEGATIVE, not linear ms².
//
// The visible label is resolved from the i18n bundle via `trends.metrics.<key>`
// at render time; this struct only carries language-independent metadata.
export interface MetricMeta {
  key: string;
  unit: string;
  sdkMin: number;
  sdkMax: number;
  /** Personal-deviation tracked? (5 metrics per SPEC-008 §3.10 (3)) */
  hasBaseline: boolean;
  color: string;
}

// Line-chart numeric metrics (TrendChart). ADR-018 / SPEC-015 widens this to
// every STORED numeric field except the 6-axis health radar (those render in
// RadarCard, not as lines) and sdk_version (string metadata, not plotted).
// hasBaseline stays true only for the 5 metrics the backend tracks
// (TrackedDeviationMetrics: hr/spo2/sbp/dbp/stress).
export const METRICS: MetricMeta[] = [
  { key: "hr", unit: "bpm", sdkMin: 50, sdkMax: 180, hasBaseline: true, color: "#00d4ff" },
  { key: "spo2", unit: "%", sdkMin: 85, sdkMax: 100, hasBaseline: true, color: "#00e5a0" },
  { key: "sbp", unit: "mmHg", sdkMin: 90, sdkMax: 180, hasBaseline: true, color: "#ff6b9d" },
  { key: "dbp", unit: "mmHg", sdkMin: 65, sdkMax: 110, hasBaseline: true, color: "#a855f7" },
  { key: "rr", unit: "rpm", sdkMin: 0, sdkMax: 60, hasBaseline: false, color: "#ffaa00" },
  { key: "hrv_rmssd", unit: "ms", sdkMin: 0, sdkMax: 600, hasBaseline: false, color: "#7ab3d0" },
  { key: "hrv_sdnn", unit: "ms", sdkMin: 0, sdkMax: 400, hasBaseline: false, color: "#7ab3d0" },
  // HRV fields added by ADR-018 (the other 4 actually uploaded).
  { key: "hrv_lf_hf", unit: "", sdkMin: 0, sdkMax: 20, hasBaseline: false, color: "#4aa3c7" },
  // hf/lf are stored as ln(HF)/ln(LF) (SDK range −4–8) — log-scale, can be negative. NOT ms².
  { key: "hrv_hf", unit: "ln(ms²)", sdkMin: -4, sdkMax: 8, hasBaseline: false, color: "#5fb8d8" },
  { key: "hrv_lf", unit: "ln(ms²)", sdkMin: -4, sdkMax: 8, hasBaseline: false, color: "#6ec5e0" },
  // mean_rr is stored in SECONDS (SDK MEAN_RR range 0–2), not ms.
  { key: "hrv_mean_rr", unit: "s", sdkMin: 0, sdkMax: 2, hasBaseline: false, color: "#88c9e6" },
  { key: "stress", unit: "", sdkMin: 0, sdkMax: 600, hasBaseline: true, color: "#ff3d5a" },
  { key: "wellness", unit: "", sdkMin: 1, sdkMax: 10, hasBaseline: false, color: "#00e5a0" },
  // Extended SDK numeric fields added by ADR-018.
  { key: "prq", unit: "", sdkMin: 0, sdkMax: 10, hasBaseline: false, color: "#ffaa00" },
  { key: "shock_index", unit: "", sdkMin: 0.25, sdkMax: 2.0, hasBaseline: false, color: "#ff8c42" },
  { key: "cardiovascular_age", unit: "yr", sdkMin: 0, sdkMax: 99, hasBaseline: false, color: "#c084fc" },
  { key: "cardiovascular_bmi", unit: "", sdkMin: 0, sdkMax: 49, hasBaseline: false, color: "#a855f7" },
  // cvd_risk stores the SDK cvd_risk.percentage (range 1–30 %), not 0–100.
  { key: "cvd_risk", unit: "%", sdkMin: 0, sdkMax: 30, hasBaseline: false, color: "#ff6b9d" },
];

// 6-axis health radar (ADR-018 / SPEC-015 §3.3; rescaled by ADR-030). These are
// NOT line metrics — they render as a single chart.js Radar card (RadarCard).
// Each axis is the native SDK 0–5 float AS STORED, under the new InfluxDB field
// prefix health_v2_* (ADR-030 removed the legacy ×20 → health_* 0–100 path).
// Labels resolve from the i18n bundle via `trends.metrics.<key>` at render time.
export interface HealthAxisMeta {
  key: string;
}

export const HEALTH_AXES: HealthAxisMeta[] = [
  { key: "health_v2_health" },
  { key: "health_v2_sleep" },
  { key: "health_v2_metabolism" },
  { key: "health_v2_equilibrium" },
  { key: "health_v2_activity" },
  { key: "health_v2_relaxation" },
];

// The radar axes are requested from the measurements endpoint alongside METRICS
// (they're not in METRICS since they don't render as lines).
export const HEALTH_AXIS_KEYS = HEALTH_AXES.map((a) => a.key);

// shade behind the line that highlights ±1 SD of the personal baseline.
export function baselineBandColor(_ctx: ScriptableContext<"line">) {
  return "rgba(0, 212, 255, 0.12)";
}
